import useApi from "@/services/useApi";
import i18n from "@/i18n";
import { showToast } from "@/helpers/showToast";

const { api } = useApi();

// the global setToastContent watcher in App.vue reads getCurrentInstance()
// inside its callback, where it is null - so toasts are raised the way
// telephonyModule does it: with the $moshaToast passed in by the component
const toast = (moshaToast, type, key) => {
    if (!moshaToast) return;

    showToast(moshaToast, type, i18n.global.t(key));
};

// [VISIBLE_VALUE, INVISIBLE_VALUE], same shape as numbersModule
const visibilityIndex = (visibility) => (visibility ? 0 : 1);

const tiktokModule = {
    namespaced: true,

    state: {
        accounts: [[], []],
        pagination: [{}, {}],
        loading: [false, false],
        searchInput: ["", ""],
    },

    getters: {
        getVisibleAccounts(state) {
            return state.accounts[0];
        },
        getInvisibleAccounts(state) {
            return state.accounts[1];
        },
        getVisibleAccountsLoading(state) {
            return state.loading[0];
        },
        getInvisibleAccountsLoading(state) {
            return state.loading[1];
        },
    },

    actions: {
        setLoading({ commit }, { value, visibility }) {
            commit("SET_LOADING", [value, visibility]);
        },

        setSearchInput({ commit }, { input, visibility }) {
            commit("SET_SEARCH_INPUT", [input, visibility]);
        },

        async loadAccounts(
            { state, commit, getters, dispatch },
            { visibility, username = null, page = 1, isCollectData = true }
        ) {
            const { headers } = useApi();
            const accountList = [];

            if (page === 1 || !isCollectData) {
                commit("SET_ACCOUNTS", [[], visibility]);
            } else {
                const collected = visibility
                    ? getters.getVisibleAccounts
                    : getters.getInvisibleAccounts;

                accountList.push(...collected);
                state.pagination[visibilityIndex(visibility)].current_page++;
            }

            dispatch("setLoading", { value: true, visibility });

            const params = {
                page,
                username,
                visibility,
            };

            await api
                .get("/api/tiktok/accounts/paginate", { headers, params })
                .then((response) => {
                    // the tiktok service answers 404 with no body when there is nothing yet
                    accountList.push(...(response.data?.data?.users ?? []));

                    commit("SET_ACCOUNTS", [accountList, visibility]);
                    commit("SET_PAGINATION", [
                        response.data?.data?.meta ?? {},
                        visibility,
                    ]);
                })
                .catch(() => {
                    commit("SET_ACCOUNTS", [accountList, visibility]);
                })
                .finally(() => {
                    dispatch("setLoading", { value: false, visibility });
                });
        },

        // the tiktok service resolves an account by user_id + account_key, so a
        // 404 here means the account belongs to a different profile account
        async fetchAccount(_, { userId }) {
            const { headers } = useApi();

            const response = await api.get(`/api/tiktok/accounts/${userId}`, {
                headers,
            });

            return response.data?.data;
        },

        // returns the tiktok OAuth link the user is then sent to
        async initiateActivation(_, { moshaToast } = {}) {
            const { headers } = useApi();

            try {
                const response = await api.post(
                    "/api/tiktok/connect",
                    {},
                    { headers }
                );
                return response.data?.data?.url;
            } catch (error) {
                toast(moshaToast, "error", "tiktok.activation.link_error");
                throw error;
            }
        },

        async updateAccount(_, { userId, moshaToast, errorKey, ...payload }) {
            const { headers } = useApi();

            try {
                const response = await api.put(
                    `/api/tiktok/accounts/${userId}`,
                    payload,
                    { headers }
                );
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", errorKey ?? "tiktok.table.update_error");
                throw error;
            }
        },

        async updateVisibility({ dispatch }, { userId, visibility, moshaToast }) {
            await dispatch("updateAccount", { userId, visibility, moshaToast });

            toast(
                moshaToast,
                "success",
                visibility
                    ? "page.integration.numbers_table.invisible_toast.available_number"
                    : "page.integration.numbers_table.invisible_toast.invisible_number"
            );
        },

        async refreshApiKey({ dispatch }, { userId, moshaToast }) {
            // the tiktok service treats api_key: true as "regenerate"
            const response = await dispatch("updateAccount", {
                userId,
                moshaToast,
                errorKey: "dialog.refresh_api_key.toast_error",
                api_key: true,
            });

            toast(moshaToast, "success", "dialog.refresh_api_key.toast_success");

            return response;
        },

        async addTag(_, { userId, name, moshaToast }) {
            const { headers } = useApi();

            try {
                const response = await api.post(
                    "/api/tiktok/tags",
                    { user_id: userId, name },
                    { headers }
                );
                toast(moshaToast, "success", "tags.add_tag_success");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "tags.add_tag_error");
                throw error;
            }
        },

        async editTag(_, { id, userId, name, moshaToast }) {
            const { headers } = useApi();

            try {
                const response = await api.put(
                    `/api/tiktok/tags/${id}`,
                    { user_id: userId, name },
                    { headers }
                );
                toast(moshaToast, "success", "tags.edit_tag_success");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "tags.edit_tag_error");
                throw error;
            }
        },

        async removeTag(_, { id, userId, moshaToast }) {
            const { headers } = useApi();

            try {
                const response = await api.delete(`/api/tiktok/tags/${id}`, {
                    headers,
                    data: { user_id: userId },
                });
                toast(moshaToast, "success", "tags.delete_tag_success");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "tags.delete_tag_error");
                throw error;
            }
        },

        async logoutAccount(_, { userId, moshaToast }) {
            const { headers } = useApi();

            try {
                const response = await api.post(
                    `/api/tiktok/accounts/${userId}/logout`,
                    {},
                    { headers }
                );
                toast(moshaToast, "success", "tiktok.logout.success");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "tiktok.logout.error");
                throw error;
            }
        },

        async saveWebhooks(_, { userId, webhooks, isNew, moshaToast }) {
            const { headers } = useApi();
            const payload = { user_id: userId, webhooks };

            try {
                const response = isNew
                    ? await api.post("/api/tiktok/webhooks", payload, { headers })
                    : await api.put("/api/tiktok/webhooks", payload, { headers });

                toast(moshaToast, "success", "dialog.webhooks.success");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "dialog.webhooks.error");
                throw error;
            }
        },

        async removeWebhooks(_, { userId, moshaToast }) {
            const { headers } = useApi();

            try {
                const response = await api.delete(
                    `/api/tiktok/webhooks/${userId}`,
                    { headers }
                );
                toast(moshaToast, "success", "dialog.webhooks.delete_success_msg");
                return response.data;
            } catch (error) {
                toast(moshaToast, "error", "dialog.webhooks.delete_error_msg");
                throw error;
            }
        },
    },

    mutations: {
        SET_ACCOUNTS(state, [data, visibility]) {
            state.accounts[visibilityIndex(visibility)] = data;
        },
        SET_PAGINATION(state, [data, visibility]) {
            state.pagination[visibilityIndex(visibility)] = data;
        },
        SET_LOADING(state, [value, visibility]) {
            state.loading[visibilityIndex(visibility)] = value;
        },
        SET_SEARCH_INPUT(state, [input, visibility]) {
            state.searchInput[visibilityIndex(visibility)] = input;
        },
    },
};

export default tiktokModule;
